import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { featureRequestApi, featureRequestFileApi, phaseApi } from '../../api/endpoints';
import { getApiErrorMessage, fetchFeatureRequestFileBlob } from '../../api/client';
import { notify } from '../../components/Notifier';
import { useAuth } from '../../auth/AuthContext';
import { useProjects } from '../../context/ProjectContext';
import { FileAttachInput } from '../../components/FileAttachInput';
import { SavedFileList } from '../../components/SavedFileList';
import { ConfirmDialog } from '../Admin/ConfirmDialog';
import { clearRequestDraft, readRequestDraft, useAutosaveRequestDraft } from './useRequestDraft';
import type { FeatureRequest, FeatureRequestFile, Phase } from '../../types';

interface RequestFormDialogProps {
  open: boolean;
  /** Có giá trị = chế độ sửa (chỉ khi status NEW), null = tạo mới. */
  request: FeatureRequest | null;
  onClose: () => void;
  onSaved: (fr: FeatureRequest) => void;
  initialProjectId?: string;
}

function generateDefaultTitle(userName: string | undefined): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  return `[${dd}${mm}${yy}] - Yêu cầu chức năng của ${userName ?? 'khách'}`;
}

/** Dialog tạo/sửa yêu cầu chức năng: tên, mô tả, dự án/giai đoạn tuỳ chọn, ảnh. */
export function RequestFormDialog({ open, request, onClose, onSaved, initialProjectId }: RequestFormDialogProps): JSX.Element {
  const { user } = useAuth();
  const { projects } = useProjects();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState<string>('');
  const [phaseId, setPhaseId] = useState<string>('');
  const [phases, setPhases] = useState<Phase[]>([]);
  const [saving, setSaving] = useState(false);
  const [existingFiles, setExistingFiles] = useState<FeatureRequestFile[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [draftPrompt, setDraftPrompt] = useState(false);

  const requestId = request?.id ?? null;

  useEffect(() => {
    if (!open) return;
    setTitle(request?.title ?? '');
    setDescription(request?.description ?? '');
    setProjectId(request?.project_id ?? initialProjectId ?? '');
    setPhaseId(request?.phase_id ?? '');
    setPendingFiles([]);
    setExistingFiles(request?.files ?? []);
    // Hỏi trước khi áp nháp cũ (nếu có) — không tự điền đè lên dữ liệu gốc
    // đang hiển thị, tránh user tưởng nhầm đó là dữ liệu đã lưu trên server.
    setDraftPrompt(readRequestDraft(user?.id ?? null, requestId) !== null);
  }, [open, request, initialProjectId]);

  useAutosaveRequestDraft(user?.id ?? null, requestId, open && !draftPrompt, {
    title,
    description,
    projectId,
    phaseId,
  });

  const handleRestoreDraft = () => {
    const draft = readRequestDraft(user?.id ?? null, requestId);
    if (draft) {
      setTitle(draft.title);
      setDescription(draft.description);
      setProjectId(draft.projectId);
      setPhaseId(draft.phaseId);
    }
    setDraftPrompt(false);
  };

  const handleDiscardDraft = () => {
    clearRequestDraft(user?.id ?? null, requestId);
    setDraftPrompt(false);
  };

  // Nạp giai đoạn của dự án đã chọn; đổi dự án thì xoá giai đoạn đang chọn —
  // tránh gửi lên tổ hợp sai mà backend sẽ 400 (CHECK phase_id thuộc project_id).
  useEffect(() => {
    if (!projectId) {
      setPhases([]);
      setPhaseId('');
      return;
    }
    let active = true;
    phaseApi
      .listByProject(projectId, { limit: 200 })
      .then((result) => {
        if (active) setPhases(result.items);
      })
      .catch(() => {
        if (active) setPhases([]);
      });
    return () => {
      active = false;
    };
  }, [projectId]);

  const handleProjectChange = (value: string) => {
    setProjectId(value);
    if (value !== request?.project_id) setPhaseId('');
  };

  const handleRemoveExisting = async (file: FeatureRequestFile) => {
    try {
      await featureRequestFileApi.remove(file.id);
      setExistingFiles((prev) => prev.filter((f) => f.id !== file.id));
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xoá file'), 'error');
    }
  };

  const handleSave = async () => {
    const resolvedTitle = title.trim().length > 0 ? title.trim() : generateDefaultTitle(user?.name);
    setSaving(true);
    try {
      const body = {
        title: resolvedTitle,
        description: description.trim(),
        project_id: projectId || null,
        phase_id: phaseId || null,
      };
      const saved = request
        ? await featureRequestApi.update(request.id, body)
        : await featureRequestApi.create(body);

      if (pendingFiles.length > 0) {
        try {
          await featureRequestFileApi.upload(saved.id, pendingFiles);
        } catch (err) {
          notify(getApiErrorMessage(err, 'Đã lưu yêu cầu nhưng upload file thất bại'), 'warning');
        }
      }

      clearRequestDraft(user?.id ?? null, requestId);
      notify(request ? 'Đã cập nhật yêu cầu' : 'Đã tạo yêu cầu chức năng', 'success');
      onSaved(saved);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu yêu cầu'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const isDirty = title.trim() || description.trim() || pendingFiles.length > 0;

  const handleDialogClose = (_event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
    // Bấm ra ngoài / Esc không được tự đóng — form có thể đang chứa nội dung
    // dài chưa lưu. Chỉ đóng qua nút "Huỷ" hoặc nút X tường minh.
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    if (!saving) handleClose();
  };

  // Đóng chủ động (nút X / "Huỷ") — hỏi xác nhận nếu có unsaved changes
  const handleClose = () => {
    if (isDirty && !window.confirm('Bỏ qua nội dung chưa lưu?')) {
      return; // Người dùng muốn tiếp tục edit
    }
    clearRequestDraft(user?.id ?? null, requestId);
    onClose();
  };

  // Chặn rời khỏi trang nếu có unsaved changes
  useEffect(() => {
    if (!open || !isDirty || saving) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [open, isDirty, saving]);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: '80vw', md: '66vw' },
          maxHeight: '90vh',
          overflow: 'auto',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {request ? 'Sửa yêu cầu chức năng' : 'Tạo yêu cầu chức năng'}
        <IconButton aria-label="Đóng" onClick={handleClose} disabled={saving} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Tên yêu cầu (không bắt buộc)"
            helperText="Để trống sẽ tự đặt tên theo ngày"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            inputProps={{ maxLength: 300 }}
          />
          <TextField
            label="Mô tả mong muốn"
            placeholder="Cứ gõ hoặc dán nguyên đoạn bạn muốn, không cần viết gọn gàng"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            size="small"
          />
          <FormControl fullWidth size="small">
            <InputLabel id="fr-project-label">Dự án (không bắt buộc)</InputLabel>
            <Select
              labelId="fr-project-label"
              label="Dự án (không bắt buộc)"
              value={projectId}
              onChange={(e) => handleProjectChange(e.target.value)}
            >
              <MenuItem value="">— Không gắn dự án —</MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" disabled={!projectId}>
            <InputLabel id="fr-phase-label">Giai đoạn (không bắt buộc)</InputLabel>
            <Select
              labelId="fr-phase-label"
              label="Giai đoạn (không bắt buộc)"
              value={phaseId}
              onChange={(e) => setPhaseId(e.target.value)}
            >
              <MenuItem value="">— Không gắn giai đoạn —</MenuItem>
              {phases.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider />
          <Typography variant="subtitle2" fontWeight={700}>
            File đính kèm
          </Typography>
          <SavedFileList
            files={existingFiles}
            fetchBlob={(f) => fetchFeatureRequestFileBlob(f.id)}
            onRemove={saving ? undefined : (f) => void handleRemoveExisting(f)}
          />
          <FileAttachInput value={pendingFiles} onChange={setPendingFiles} disabled={saving} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Huỷ
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Đang lưu…' : 'Lưu'}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={draftPrompt}
        title="Khôi phục nội dung chưa lưu?"
        message="Phát hiện nội dung bạn gõ dở lần trước nhưng chưa lưu. Khôi phục lại không?"
        confirmLabel="Khôi phục"
        confirmColor="primary"
        onConfirm={handleRestoreDraft}
        onCancel={handleDiscardDraft}
      />
    </Dialog>
  );
}
